import {
  type Chart,
  type ScriptableTooltipContext,
  type TooltipItem,
  type TooltipModel,
} from "chart.js";
import moment, { type Moment } from "moment";

import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { type User } from "@internal/user";
import { MetricFormatters } from "@north-beam/nb-client-side";

import { KpiType } from "../../explore-budgets/client-setting/types";

import { getChartData } from "./plugins/chart-data-manager-plugin";
import {
  MODEL_FIT_ACTUAL_ID,
  MODEL_FIT_PREDICTION_HDI_ID,
  MODEL_FIT_PREDICTION_ID,
  MODEL_FIT_SEASONALITY_ID,
  MODEL_FIT_SPEND_ID,
} from "./utils/chart-dataset-helpers";

const DIVIDER_HTML =
  "<div class='border-t border-v2-secondary h-1 my-2 w-full'></div>";

export function chartTooltip(
  context: ScriptableTooltipContext<"line">,
  kpiType: KpiType,
) {
  const chart = context.chart;
  const tooltip = context.tooltip;
  const options = context.tooltip.options as any;
  const isRevenue = kpiType === KpiType.REVENUE;

  let tooltipEl = document.getElementById("chartjs-tooltip");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    document.body.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (tooltip.body && tooltip.dataPoints.length) {
    const content = buildTooltipContent(tooltip, isRevenue, options);
    const startDateMoment = moment(tooltip.dataPoints[0].parsed.x);
    const startDate = startDateMoment.format("ddd, MMM DD");
    const endDateMoment = startDateMoment.clone().add(6, "days");
    const endDate = endDateMoment.format("ddd, MMM DD YYYY");

    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[-30px]",
      center: "translate-x-[50%]",
      right: "translate-x-[30px]",
    }[tooltip.xAlign];

    tooltipEl.innerHTML = `
      <div class="relative">
        <div
          data-tooltip-id="content"
          style="min-width: 330px;"
          class="
            bg-v2-primary border border-v2-secondary py-5 px-4 rounded-md space-y-2.5
            shadow whitespace-nowrap ${contentPosition}
          "
        >
          <div class="text-lg font-bold leading-0 py-2">${
            options.tooltipTitle
          }</div>
          <div class="text-xs text-gray+1">${startDate} - ${endDate}</div>
          ${content}
          ${calendarOfEventsContent(chart, startDateMoment, endDateMoment)}
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-v2-primary absolute bottom-[-6px] border-t border-l border-v2-secondary
            ${arrowPosition} rotate-[225deg]
          "
        >
        </div>
      </div>
    `;
  }

  const position = chart.canvas.getBoundingClientRect();

  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: `translate(${translateX}, -100%)`,
    top: `${position.top + window.scrollY + tooltip.caretY}px`,
    left: `${position.left + window.scrollX + tooltip.caretX}px`,
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();

    if (contentRect.left < 0) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("left-0", "translate-x-[-50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[-30px]");
      tooltipEl.style.transform = "translate(0%, -100%)";
    } else if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("right-0", "translate-x-[50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[30px]");
    }
  }
}

const buildTooltipContent = (
  tooltip: TooltipModel<"line" | "bar">,
  isRevenue: boolean,
  options: { tooltipHdiProb: number; user: User },
) => {
  const content = [];

  const actual = tooltip.dataPoints.find(
    ({ dataset }) => dataset.label === MODEL_FIT_ACTUAL_ID,
  );
  const predicted = tooltip.dataPoints.find(
    ({ dataset }) => dataset.label === MODEL_FIT_PREDICTION_ID,
  );
  const seasonality = tooltip.dataPoints.find(
    ({ dataset }) => dataset.label === MODEL_FIT_SEASONALITY_ID,
  );
  const predictedHdi = tooltip.dataPoints.filter(
    ({ dataset }) => dataset.label === MODEL_FIT_PREDICTION_HDI_ID,
  );
  const spend = tooltip.dataPoints.find(
    ({ dataset }) => dataset.label === MODEL_FIT_SPEND_ID,
  );

  if (actual) {
    content.push(buildActualLegend(options, isRevenue, actual, predicted));
  }

  if (predicted) {
    content.push(
      buildPredictedLegend(options, isRevenue, predicted, predictedHdi),
    );
  }

  if (seasonality) {
    content.push(
      buildSeasonalityLegend(options, { ...seasonality, isRevenue }),
    );
  }

  if (spend) {
    content.push(buildSpendLegend(options, { ...spend }));
  }

  return `
    <div class="flex flex-col text-xs space-y-3">
      ${content.join(DIVIDER_HTML)}
    </div>
  `;
};

const buildActualLegend = (
  options: { tooltipHdiProb: number; user: User },
  isRevenue: boolean,
  actual: TooltipItem<"line">,
  predicted?: TooltipItem<"line">,
) => {
  const legend = buildLegend(
    {
      borderColor: actual.dataset.borderColor,
      backgroundColor: actual.dataset.backgroundColor,
      borderDash: actual.dataset.borderDash ?? [],
      borderWidth: 4,
    } as any,
    false,
  );

  const formattedValueTotal = isRevenue
    ? MetricFormatters.currency(
        actual.parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )
    : MetricFormatters.decimal(
        actual.parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      );

  let content = `
    <div class="flex space-x-0.5 justify-between">
      <div class="flex space-x-0.5 items-center">
        <div style="width: 22px;">${legend}</div>
        <div>${actual.dataset.label}</div>
      </div>
      <div class="text-right font-mono">${formattedValueTotal}</div>
    </div>
  `;

  if (predicted) {
    const diff = (actual.raw as any).y / (predicted.raw as any).y - 1;
    const diffFormatted = MetricFormatters.percentage(
      Math.abs(diff),
      options.user.currencies.currentCurrency,
      {
        precision: "approximate",
      },
    );

    content += `
      <div class="text-gray+1 ml-6">
        <span class="font-bold">${diffFormatted}</span>
        <span>${diff > 0 ? "Above" : "Below"} predicted</span>
      </div>
    `;
  }

  return content;
};

const buildPredictedLegend = (
  options: { tooltipHdiProb: number; user: User },
  isRevenue: boolean,
  { dataset, parsed }: TooltipItem<"line">,
  hdis: TooltipItem<"line">[],
) => {
  const legend = buildLegend(
    {
      borderColor: dataset.borderColor,
      backgroundColor: dataset.backgroundColor,
      borderDash: dataset.borderDash ?? [],
      borderWidth: 4,
    } as any,
    false,
  );

  const predictedLegend = buildLegend(
    {
      borderColor: "#D5D9FE",
      backgroundColor: "#D5D9FE",
      borderDash: [],
    } as any,
    true,
  );

  const hdiMin = Math.min(...hdis.map((e) => e.parsed.y));
  const hdiMax = Math.max(...hdis.map((e) => e.parsed.y));

  const hdiMinFormatted = isRevenue
    ? MetricFormatters.currency(
        Math.abs(hdiMin),
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )
    : MetricFormatters.decimal(
        Math.abs(hdiMin),
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      );
  const hdiMaxFormatted = isRevenue
    ? MetricFormatters.currency(
        Math.abs(hdiMax),
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )
    : MetricFormatters.decimal(
        Math.abs(hdiMax),
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      );

  const formattedValueTotal = isRevenue
    ? MetricFormatters.currency(
        parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )
    : MetricFormatters.decimal(
        parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      );
  return `
    <div class="flex space-x-0.5 justify-between mb-2">
      <div class="flex space-x-0.5 items-center">
        <div style="width: 22px;">${legend}</div>
        <div>${dataset.label}</div>
      </div>
      <div class="text-right font-mono">${formattedValueTotal}</div>
    </div>
    <div class="flex space-x-0.5 justify-between">
      <div class="flex space-x-0.5 items-center">
        <div style="width: 22px;">${predictedLegend}</div>
        <div>Predicted (${(options.tooltipHdiProb || 0) * 100}% HDI)</div>
      </div>
      <div class="text-right font-mono">${hdiMinFormatted} - ${hdiMaxFormatted}</div>
    </div>
  `;
};

const buildSeasonalityLegend = (
  options: { tooltipHdiProb: number; user: User },
  { dataset, isRevenue, parsed }: TooltipItem<"line"> & { isRevenue: boolean },
) => {
  const legend = buildLegend(
    {
      borderColor: dataset.borderColor,
      backgroundColor: dataset.backgroundColor,
      borderDash: dataset.borderDash ?? [],
      borderWidth: 4,
    } as Parameters<typeof buildLegend>[0],
    false,
  );
  const formattedValueTotal = isRevenue
    ? MetricFormatters.currency(
        parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      )
    : MetricFormatters.decimal(
        parsed.y,
        options.user.currencies.currentCurrency,
        {
          precision: "approximate",
        },
      );

  return `
    <div class="flex space-x-0.5 justify-between mb-2">
      <div class="flex space-x-0.5 items-center">
        <div style="width: 22px;">${legend}</div>
        <div>${dataset.label}</div>
      </div>
      <div class="text-right font-mono">${formattedValueTotal}</div>
    </div>
  `;
};

const buildSpendLegend = (
  options: { tooltipHdiProb: number; user: User },
  { dataset, parsed }: TooltipItem<"line">,
) => {
  const legend = buildLegend(
    {
      borderColor: dataset.borderColor,
      backgroundColor: dataset.backgroundColor,
      borderDash: dataset.borderDash ?? [],
      borderWidth: 4,
    } as Parameters<typeof buildLegend>[0],
    false,
  );
  const formattedValueTotal = MetricFormatters.currency(
    parsed.y,
    options.user.currencies.currentCurrency,
    {
      precision: "approximate",
    },
  );

  return `
    <div class="flex space-x-0.5 justify-between mb-2">
      <div class="flex space-x-0.5 items-center">
        <div style="width: 22px;">${legend}</div>
        <div>${dataset.label}</div>
      </div>
      <div class="text-right font-mono">${formattedValueTotal}</div>
    </div>
  `;
};

const getCalendarOfEvents = (
  chart: Chart<"line">,
  startDate: Moment,
  endDate: Moment,
) => {
  const chartDataManager = getChartData(chart);
  return (
    chartDataManager?.calendarOfEvents.filter(
      (coe) =>
        moment(coe.annotatedDate).isSameOrBefore(endDate) &&
        moment(startDate).isSameOrBefore(coe.endDate),
    ) ?? []
  );
};

const calendarOfEventsContent = (
  chart: Chart<"line">,
  startDateMoment: Moment,
  endDateMoment: Moment,
) => {
  const calendarOfEvents = getCalendarOfEvents(
    chart,
    startDateMoment,
    endDateMoment,
  );

  if (calendarOfEvents.length > 0) {
    return `
      ${DIVIDER_HTML}
      <div class="flex items-center text-xs">
        <span class="h-4.5 w-4.5 flex-center bg-venus text-white font-bold font-mono rounded-full">${
          calendarOfEvents.length
        }</span>
        <span class="ml-2">Calendar of Events</span>
      </div>
      ${calendarOfEvents
        .map(
          ({ description, annotatedDate, endDate }) =>
            `
            <div class="flex flex-col space-y-1 text-xs">
              <div class="text-gray+1">
              ${moment(annotatedDate).format("ddd, MMM DD")} - ${moment(
              endDate,
            ).format("ddd, MMM DD")}</div>
              <div class="font-bold">${description}</div>
            </div>
        `,
        )
        .join("")}
    `;
  }

  return "";
};
